import React, { useEffect } from "react";
import ResponsiveNavbar from "../../components/Header";
import Footer from "../../components/Footer";

const Incubators = () => {
  useEffect(() => {
    // Add smooth scroll behavior
    document.documentElement.style.scrollBehavior = "smooth";

    // Add animation on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  return (
    <>
      <ResponsiveNavbar />

      <div className="incubators-page">
        {/* Hero Section with Background Image */}
        <section className="hero-section relative h-[70vh] md:h-[80vh] flex items-center justify-center overflow-hidden mt-16">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('/assets/incubators/Hero_image.webp')",
              filter: "brightness(0.5)",
            }}
          />

          {/* Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />

          {/* Hero Content */}
          <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 animate-fade-in-down">
              MAKERSPACE
            </h1>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-6 tracking-wider animate-fade-in-up animation-delay-200">
              IMAGINE | ENGINEER | PROTOTYPE | LAUNCH
            </h2>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 text-[#00AEEF] animate-fade-in animation-delay-400">
              For Incubators
            </h3>
            <p className="text-lg md:text-xl lg:text-2xl max-w-4xl mx-auto leading-relaxed animate-fade-in animation-delay-600">
              Ecosystems built for entrepreneurs, researchers, and startups to
              turn ideas into reality faster.
            </p>
          </div>
        </section>

        {/* Main Description */}
        <section className="description-section py-10 md:py-12 px-4 md:px-8 lg:px-16 bg-white">
          <div className="max-w-6xl mx-auto text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
            <p className="text-lg md:text-xl lg:text-xl text-gray-700 leading-relaxed">
              We build <span className="font-bold">high-velocity innovation ecosystems</span> where <span className="font-bold">entrepreneurs, researchers, and startups</span> can turn ideas into tested, <span className="font-bold">faster market-ready products</span>. With the <span className="font-bold">right tools, space, and support</span> under one roof they <span className="font-bold">prototype, validate, and scale</span> with confidence and impact.
            </p>
          </div>
        </section>

        {/* Why Incubators Need a Makerspace */}
        <section className="why-section py-12 md:py-14 px-4 md:px-8 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 md:mb-10 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                <span className="text-[#00AEEF]">Why Incubators</span> Need a Makerspace
                <div className="h-1 w-24 bg-[#00AEEF] mt-4"></div>
              </h2>
            </div>

            <p className="text-lg md:text-xl text-gray-700 mb-6 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
              Because without a build test space, incubators are only growing ideas, not products.
            </p>

            <p className="text-lg font-semibold text-gray-900 mb-8 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
              A makerspace-driven ecosystem helps incubators
            </p>

            {/* Benefits with icons and image on right */}
            <div className="grid md:grid-cols-2 gap-10 lg:gap-12 items-start">
              {/* Left side - Benefits */}
              <div className="space-y-8 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
                {/* Benefit 1 */}
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-white border-4 border-blue-600 shadow-lg">
                      <img
                        src="/assets/incubators/7.webp"
                        alt="Convert talk"
                        className="h-10 w-10 object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Convert talk into tangible prototypes
                    </h3>
                  </div>
                </div>

                {/* Benefit 2 */}
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-white border-4 border-blue-600 shadow-lg">
                      <img
                        src="/assets/incubators/6.webp"
                        alt="Speed up validation"
                        className="h-10 w-10 object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Speed up validation and pivots
                    </h3>
                  </div>
                </div>

                {/* Benefit 3 */}
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-white border-4 border-blue-600 shadow-lg">
                      <img
                        src="/assets/incubators/8.webp"
                        alt="Show real outcomes"
                        className="h-10 w-10 object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Show real, demo-ready outcomes to mentors, investors, and grant agencies.
                    </h3>
                  </div>
                </div>
              </div>

              {/* Right side - Image */}
              <div className="hidden md:flex items-center justify-center animate-on-scroll opacity-0 translate-x-10 transition-all duration-1000">
                <div className="w-full max-w-xs">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-100/40 to-cyan-100/40 rounded-2xl blur-2xl"></div>
                    <img
                      src="/assets/incubators/Illustrator.webp"
                      alt="Makerspace in action"
                      className="relative w-full h-auto rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom summary */}
            <div className="mt-10 pt-6 border-t border-gray-200 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
              <p className="text-lg text-gray-900 mb-8">
                <span className="font-bold">In short,</span> it <span className="font-bold">upgrades the incubator from a co-working + mentoring space to a true product-launch engine.</span>
              </p>

              {/* 5 Benefits with icons */}
              <div className="flex flex-wrap justify-center items-center gap-4 md:gap-3 lg:gap-4">
                {/* Benefit 1 - Faster idea-to-prototype */}
                <div className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-20 h-20 rounded-full border-4 border-[#00AEEF] bg-blue-50 flex items-center justify-center shadow-lg">
                      <img
                        src="/assets/incubators/1.webp"
                        alt="Faster idea-to-prototype"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </div>
                  <p className="text-sm md:text-base font-semibold text-gray-900">
                    Faster<br />idea-to-prototype
                  </p>
                </div>

                {/* Connector 1 */}
                <div className="hidden md:flex items-center">
                  <div className="w-6 h-0.5 bg-[#00AEEF]"></div>
                  <span className="text-[#00AEEF] text-lg mx-0.5">•</span>
                  <div className="w-6 h-0.5 bg-[#00AEEF]"></div>
                </div>

                {/* Benefit 2 - Rapid testing & iteration */}
                <div className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-20 h-20 rounded-full border-4 border-[#00AEEF] bg-blue-50 flex items-center justify-center shadow-lg">
                      <img
                        src="/assets/incubators/2.webp"
                        alt="Rapid testing & iteration"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </div>
                  <p className="text-sm md:text-base font-semibold text-gray-900">
                    Rapid testing &<br />iteration
                  </p>
                </div>

                {/* Connector 2 */}
                <div className="hidden md:flex items-center">
                  <div className="w-6 h-0.5 bg-[#00AEEF]"></div>
                  <span className="text-[#00AEEF] text-lg mx-0.5">•</span>
                  <div className="w-6 h-0.5 bg-[#00AEEF]"></div>
                </div>

                {/* Benefit 3 - More fundable startups */}
                <div className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-20 h-20 rounded-full border-4 border-[#00AEEF] bg-blue-50 flex items-center justify-center shadow-lg">
                      <img
                        src="/assets/incubators/3.webp"
                        alt="More fundable startups"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </div>
                  <p className="text-sm md:text-base font-semibold text-gray-900">
                    More fundable<br />startups
                  </p>
                </div>

                {/* Connector 3 */}
                <div className="hidden md:flex items-center">
                  <div className="w-6 h-0.5 bg-[#00AEEF]"></div>
                  <span className="text-[#00AEEF] text-lg mx-0.5">•</span>
                  <div className="w-6 h-0.5 bg-[#00AEEF]"></div>
                </div>

                {/* Benefit 4 - Stronger innovation ecosystem */}
                <div className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-20 h-20 rounded-full border-4 border-[#00AEEF] bg-blue-50 flex items-center justify-center shadow-lg">
                      <img
                        src="/assets/incubators/4.webp"
                        alt="Stronger innovation ecosystem"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </div>
                  <p className="text-sm md:text-base font-semibold text-gray-900">
                    Stronger innovation<br />ecosystem
                  </p>
                </div>

                {/* Connector 4 */}
                <div className="hidden md:flex items-center">
                  <div className="w-6 h-0.5 bg-[#00AEEF]"></div>
                  <span className="text-[#00AEEF] text-lg mx-0.5">•</span>
                  <div className="w-6 h-0.5 bg-[#00AEEF]"></div>
                </div>

                {/* Benefit 5 - Attracts serious innovators */}
                <div className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-20 h-20 rounded-full border-4 border-[#00AEEF] bg-blue-50 flex items-center justify-center shadow-lg">
                      <img
                        src="/assets/incubators/5.webp"
                        alt="Attracts serious innovators"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </div>
                  <p className="text-sm md:text-base font-semibold text-gray-900">
                    Attracts serious<br />innovators
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Build */}
        <section className="what-we-build-section py-12 md:py-14 px-4 md:px-8 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-10 items-center">
              {/* Left side - Dark background with content */}
              <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-3xl p-8 md:p-10 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
                <h2 className="text-3xl md:text-4xl font-bold mb-2">
                  <span className="text-[#00AEEF]">What</span> <span className="text-white">We Build</span>
                  <div className="h-1 w-20 bg-[#00AEEF] mt-4"></div>
                </h2>
                
                <h3 className="text-xl md:text-2xl font-semibold text-white mb-6 mt-4">
                  A Complete Product Development Ecosystem
                </h3>

                {/* Features with icons */}
                <div className="space-y-5">
                  {/* Tools to prototype */}
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-[#00AEEF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.658 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-semibold">Tools to prototype</p>
                      <div className="h-px w-32 bg-gray-600 mt-2"></div>
                    </div>
                    <div className="flex-shrink-0 ml-auto">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                  </div>

                  {/* Space to experiment */}
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-[#00AEEF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-semibold">Space to experiment</p>
                      <div className="h-px w-32 bg-gray-600 mt-2"></div>
                    </div>
                    <div className="flex-shrink-0 ml-auto">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                    </div>
                  </div>

                  {/* Systems to validate */}
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-[#00AEEF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-semibold">Systems to validate</p>
                      <div className="h-px w-32 bg-gray-600 mt-2"></div>
                    </div>
                    <div className="flex-shrink-0 ml-auto">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Support to scale */}
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-[#00AEEF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-semibold">Support to scale</p>
                      <div className="h-px w-32 bg-gray-600 mt-2"></div>
                    </div>
                    <div className="flex-shrink-0 ml-auto">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h-2m0 0h-2m2 0V8m0 2v2M7 14h10a2 2 0 002-2V4a2 2 0 00-2-2H7a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - Image */}
              <div className="hidden md:flex items-center justify-center animate-on-scroll opacity-0 translate-x-10 transition-all duration-1000">
                <div className="relative w-full max-w-md">
                  {/* Decorative background blur */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#00AEEF]/10 to-blue-200/10 rounded-3xl blur-2xl"></div>
                  
                  {/* Image container with frame effect */}
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl transform transition duration-700 hover:shadow-3xl hover:scale-105 h-[600px] md:h-[440px]">
                    <img
                      src="/assets/incubators/Banner 2.0.webp"
                      alt="What We Build"
                      className="w-full h-full object-cover block"
                    />

                    {/* Subtle overlay for better appearance */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Infrastructure and Equipment */}
        <section className="infrastructure-section py-12 md:py-14 px-4 md:px-8 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-10 md:mb-12 text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
              Infrastructure and Equipment
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Equipment 1 */}
              <div className="equipment-card group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 bg-white animate-on-scroll opacity-0 translate-y-10">
                <div className="mb-4 flex justify-center">
                  <div className="w-48 h-48 flex items-center justify-center p-2 border-4 border-[#00AEEF] rounded-xl">
                    <img
                      src="/assets/incubators/NITTE_AICT (42).webp"
                      alt="3D Printing"
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
                <h3 className="font-bold text-xl mb-2 text-gray-900 text-center">
                  3D Printing & Fabrication
                </h3>
                <p className="text-gray-600 text-center leading-relaxed text-sm">
                  Industrial-grade 3D printers, laser cutters, & CNC machines
                </p>
              </div>

              {/* Equipment 2 */}
              <div className="equipment-card group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 bg-white animate-on-scroll opacity-0 translate-y-10 animation-delay-100">
                <div className="mb-4 flex justify-center">
                  <div className="w-48 h-48 flex items-center justify-center p-2 border-4 border-[#00AEEF] rounded-xl">
                    <img
                      src="/assets/incubators/NITTE_AICT (36).webp"
                      alt="CNC & PCB"
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
                <h3 className="font-bold text-xl mb-2 text-gray-900 text-center">
                  CNC & PCB
                </h3>
                <p className="text-gray-600 text-center leading-relaxed text-sm">
                  CNC milling and PCB fabrication systems
                </p>
              </div>

              {/* Equipment 3 */}
              <div className="equipment-card group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 bg-white animate-on-scroll opacity-0 translate-y-10 animation-delay-200">
                <div className="mb-4 flex justify-center">
                  <div className="w-48 h-48 flex items-center justify-center p-2 border-4 border-[#00AEEF] rounded-xl">
                    <img
                      src="/assets/incubators/NITTE_AICT (19).webp"
                      alt="IoT & Electronics"
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
                <h3 className="font-bold text-xl mb-2 text-gray-900 text-center">
                  IoT & Electronics
                </h3>
                <p className="text-gray-600 text-center leading-relaxed text-sm">
                  IoT, electronics, & robotics development stations
                </p>
              </div>

              {/* Equipment 4 */}
              <div className="equipment-card group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 bg-white animate-on-scroll opacity-0 translate-y-10 animation-delay-300">
                <div className="mb-4 flex justify-center">
                  <div className="w-48 h-48 flex items-center justify-center p-2 border-4 border-[#00AEEF] rounded-xl">
                    <img
                      src="/assets/incubators/Workstations.webp"
                      alt="Startup Workstations"
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
                <h3 className="font-bold text-xl mb-2 text-gray-900 text-center">
                  Startup Workstations
                </h3>
                <p className="text-gray-600 text-center leading-relaxed text-sm">
                  Startup workstations & product testing labs
                </p>
              </div>

              {/* Equipment 5 */}
              <div className="equipment-card group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 bg-white animate-on-scroll opacity-0 translate-y-10 animation-delay-400">
                <div className="mb-4 flex justify-center">
                  <div className="w-48 h-48 flex items-center justify-center p-2 border-4 border-[#00AEEF] rounded-xl">
                    <img
                      src="/assets/incubators/CAD-Design-and-Software.webp"
                      alt="Design Software"
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
                <h3 className="font-bold text-xl mb-2 text-gray-900 text-center">
                  Design Software
                </h3>
                <p className="text-gray-600 text-center leading-relaxed text-sm">
                  Design simulation & product modeling software
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Image Showcase */}
        <section className="py-8 md:py-10 px-4 sm:px-6 lg:px-12 bg-white">
          <div className="w-full">
            <div className="overflow-hidden rounded-2xl shadow-xl">
              <img
                src="/assets/incubators/Banner2.1.webp"
                alt="Makerspace Workshop"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="how-we-work-section py-12 md:py-14 px-4 md:px-8 lg:px-16 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-10 md:mb-12 text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
              How We Work
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Step 1 - Discover */}
              <div className="step-card text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 animate-on-scroll opacity-0 translate-y-10">
                <div className="w-40 h-40 mx-auto mb-4 transform transition-transform duration-300 hover:scale-110 border-4 border-[#00AEEF] rounded-xl overflow-hidden flex items-center justify-center">
                  <img
                    src="/assets/incubators/NITTE_AICT (27).webp"
                    alt="Discover"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-bold text-2xl mb-3 text-gray-900">
                  Discover
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  We understand your goals, users and the kind of innovation you
                  want to enable
                </p>
              </div>

              {/* Step 2 - Design */}
              <div className="step-card text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 animate-on-scroll opacity-0 translate-y-10 animation-delay-100">
                <div className="w-40 h-40 mx-auto mb-4 transform transition-transform duration-300 hover:scale-110 border-4 border-[#00AEEF] rounded-xl overflow-hidden flex items-center justify-center">
                  <img
                    src="/assets/incubators/3D-View.webp"
                    alt="Design"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-bold text-2xl mb-3 text-gray-900">
                  Design
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  We create a tailored makerspace ecosystem—tools, layout, and
                  workflow included
                </p>
              </div>

              {/* Step 3 - Build */}
              <div className="step-card text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 animate-on-scroll opacity-0 translate-y-10 animation-delay-200">
                <div className="w-40 h-40 mx-auto mb-4 transform transition-transform duration-300 hover:scale-110 border-4 border-[#00AEEF] rounded-xl overflow-hidden flex items-center justify-center">
                  <img
                    src="/assets/incubators/NITTE_AICT (14).webp"
                    alt="Build"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-bold text-2xl mb-3 text-gray-900">Build</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  We setup the entire space end-to-end with seamless
                  installation and integration
                </p>
              </div>

              {/* Step 4 - Enable */}
              <div className="step-card text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 animate-on-scroll opacity-0 translate-y-10 animation-delay-300">
                <div className="w-40 h-40 mx-auto mb-4 transform transition-transform duration-300 hover:scale-110 border-4 border-[#00AEEF] rounded-xl overflow-hidden flex items-center justify-center">
                  <img
                    src="/assets/incubators/Enable.webp"
                    alt="Enable"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-bold text-2xl mb-3 text-gray-900">
                  Enable
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  We train and support your teams so that innovators can start
                  building and scale fast
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Aligned with India's Startup Mission */}
        <section className="mission-section py-12 md:py-14 px-4 md:px-8 lg:px-16 bg-gradient-to-br from-orange-50 to-orange-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
              Aligned with India's Startup Mission
            </h2>

            {/* Icon - Make in India */}
            <div className="flex justify-center mb-6 animate-on-scroll opacity-0 scale-95 transition-all duration-1000">
              <img
                src="/assets/incubators/Make_In_India.webp"
                alt="Make in India"
                className="h-24 md:h-32 object-contain"
              />
            </div>

            <ul className="space-y-5 md:space-y-6">
              <li className="flex items-start group animate-on-scroll opacity-0 translate-x-10 transition-all duration-700">
                <span className="flex-shrink-0 w-12 h-12 bg-[#00AEEF] rounded-full flex items-center justify-center text-white text-2xl mr-5 group-hover:scale-110 transition-transform duration-300">
                  ✓
                </span>
                <span className="text-lg md:text-xl text-gray-700 pt-1">
                  Drives product innovation and prototype development
                </span>
              </li>
              <li className="flex items-start group animate-on-scroll opacity-0 translate-x-10 transition-all duration-700 animation-delay-100">
                <span className="flex-shrink-0 w-12 h-12 bg-[#00AEEF] rounded-full flex items-center justify-center text-white text-2xl mr-5 group-hover:scale-110 transition-transform duration-300">
                  ✓
                </span>
                <span className="text-lg md:text-xl text-gray-700 pt-1">
                  Strengthens the startup and incubation ecosystem
                </span>
              </li>
              <li className="flex items-start group animate-on-scroll opacity-0 translate-x-10 transition-all duration-700 animation-delay-200">
                <span className="flex-shrink-0 w-12 h-12 bg-[#00AEEF] rounded-full flex items-center justify-center text-white text-2xl mr-5 group-hover:scale-110 transition-transform duration-300">
                  ✓
                </span>
                <span className="text-lg md:text-xl text-gray-700 pt-1">
                  Enables research-to-market transformation
                </span>
              </li>
              <li className="flex items-start group animate-on-scroll opacity-0 translate-x-10 transition-all duration-700 animation-delay-300">
                <span className="flex-shrink-0 w-12 h-12 bg-[#00AEEF] rounded-full flex items-center justify-center text-white text-2xl mr-5 group-hover:scale-110 transition-transform duration-300">
                  ✓
                </span>
                <span className="text-lg md:text-xl text-gray-700 pt-1">
                  Supports Make in India & Atmanirbhar Bharat initiatives
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* Our Supporters */}
        <section className="supporters-section py-12 md:py-14 px-4 md:px-8 lg:px-16 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
              Our Supporters
            </h2>

            {/* Supporters Display - Single Image - Larger */}
            <div className="animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000 flex justify-center">
              <div className="transform hover:scale-105 transition-transform duration-300">
                <img
                  src="/assets/incubators/Our-Partners.webp"
                  alt="Our Partners"
                  className="h-40 md:h-56 lg:h-64 object-contain"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact-section py-10 md:py-12 px-4 md:px-8 lg:px-16 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="max-w-6xl mx-auto animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Contact Us
            </h2>

            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {/* Phone */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-[#00AEEF] rounded-full flex items-center justify-center mb-3">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                  <p className="text-sm text-gray-600">+91-74832 76508</p>
                  <p className="text-sm text-gray-600">+91-95610 47976</p>
                </div>

                {/* Email */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-[#00AEEF] rounded-full flex items-center justify-center mb-3">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                  <p className="text-sm text-[#00AEEF] font-medium">
                    admin@experimindlabs.com
                  </p>
                </div>

                {/* Website */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-[#00AEEF] rounded-full flex items-center justify-center mb-3">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                      />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Website</h3>
                  <a
                    href="https://www.experimindlabs.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#00AEEF] font-medium hover:underline"
                  >
                    www.experimindlabs.com
                  </a>
                </div>
              </div>

              {/* Locations */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 text-center">
                  Our Locations
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <svg
                      className="w-5 h-5 text-[#00AEEF] mt-1 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Atal Block, NMAMIT Campus, Nitte, Karkala - 574110
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <svg
                      className="w-5 h-5 text-[#00AEEF] mt-1 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      RCIIF, Sector 4, Kharghar, Navi Mumbai, Maharashtra 410210
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      <style jsx>{`
        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fade-in-down {
          animation: fade-in-down 1s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animation-delay-100 {
          animation-delay: 0.1s;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-300 {
          animation-delay: 0.3s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .animation-delay-500 {
          animation-delay: 0.5s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }

        .animate-visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
          transform: translateX(0) !important;
          transform: scale(1) !important;
        }
      `}</style>
    </>
  );
};

export default Incubators;